import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import './App.css';
import { useGameStore } from './state/gameStore';
import { useAuthStore } from './state/authStore';
import {
  createAccount, createCharacter, deleteCharacter,
  listCharacters, loadAccount, loadCharacter,
  markDirty, setActiveCharacter, startSync, stopSync, flushNow,
} from './state/profileSync';
import {
  addLocalCharacter, deleteLocalCharacter, getActiveLocalCharacterId,
  listLocalCharacters, loadLocalCharacter,
  setActiveLocalCharacter, syncActiveCharacter,
} from './state/localCharacters';
import { installOnlineFlushHook as installTelemetryOnlineFlushHook } from './state/telemetry';
import { isSupabaseConfigured } from './lib/supabase';
import { AuthScreen } from './ui/screens/AuthScreen';
import { CharacterSelectScreen } from './ui/screens/CharacterSelectScreen';
import { NewCharacterScreen } from './ui/screens/NewCharacterScreen';
import { MigrationScreen } from './ui/screens/MigrationScreen';
import { TitleScreen } from './ui/TitleScreen';
import { AppShell } from './ui/shell/AppShell';
import { isTabUnlocked, type Tab } from './ui/shell/BottomNav';
import { TerminalScreen } from './ui/screens/TerminalScreen';
import { DungeonScreen } from './ui/screens/DungeonScreen';
import { MarketScreen } from './ui/screens/MarketScreen';
import { CharacterScreen } from './ui/screens/CharacterScreen';
import { FeedbackScreen } from './ui/screens/FeedbackScreen';
import { LeaderboardScreen } from './ui/screens/LeaderboardScreen';
// Dev-only 3D playground — never opened in normal flow, lazy-load to keep it
// out of the initial bundle.
const Test3DScreen = lazy(() =>
  import('./ui/screens/Test3DScreen').then((m) => ({ default: m.Test3DScreen })),
);
const TuneStudioScreen = lazy(() =>
  import('./ui/screens/TuneStudioScreen').then((m) => ({ default: m.TuneStudioScreen })),
);
import { createPlayer, isUuid, randomId } from './domain/player';
import { ITEMS } from './domain/items';
import type { CharacterClass, CharacterSummary, EquipmentInstance, Player } from './domain/types';
import { IntroLoader } from './ui/components/IntroLoader';
import { PENDING_CONVERT_KEY, PENDING_SWITCH_KEY } from './ui/components/SettingsModal';
import { MAX_CHARACTER_SLOTS } from './state/profileSync';

/**
 * Replace any non-UUID equipment IDs with fresh UUIDs.
 * Needed for legacy local saves where IDs were like `eq_a1b2c3d4`.
 * Returns true if any IDs were replaced (caller should re-sync to server).
 */
function sanitizeEquipmentIds(player: Player): boolean {
  let changed = false;
  const fix = (inst: EquipmentInstance): EquipmentInstance => {
    if (isUuid(inst.id)) return inst;
    changed = true;
    return { ...inst, id: randomId() };
  };
  for (const slot of Object.keys(player.equipment) as Array<keyof typeof player.equipment>) {
    const inst = player.equipment[slot];
    if (inst) player.equipment[slot] = fix(inst);
  }
  player.equipmentStash = player.equipmentStash.map(fix);
  return changed;
}

/**
 * One-time backfill for the shield slot (added 2026-05). If the player has a
 * legacy shield sitting in their stash (vault_shield etc., previously equipped
 * but unequipped during migration), re-equip it. Otherwise leave the slot
 * empty — under the HK-only progression model the shield is an earned drop,
 * not auto-granted. Returns true if changed.
 */
function backfillShield(player: Player): boolean {
  if (player.equipment.shield) return false;
  const stashed = player.equipmentStash.find((inst) =>
    inst.itemId === 'hk_shield' || inst.itemId === 'starter_shield'
    || inst.itemId === 'heart_shield' || inst.itemId === 'vault_shield',
  );
  if (!stashed) return false;
  player.equipment.shield = stashed;
  player.equipmentStash = player.equipmentStash.filter((i) => i.id !== stashed.id);
  return true;
}

/**
 * Catalog migration (2026-05 HK-only cleanup). The item catalog now contains
 * only Hollow-Knight set pieces + legendary weapons + the amulet + resources.
 * This map rewrites any legacy item IDs (including the previous starter_* and
 * vault_* tiers) to their HK / legendary equivalent so old saves keep
 * upgrade level + rolledStats without rendering as "?".
 */
const LEGACY_ITEM_REPLACEMENTS: Record<string, string> = {
  // Removed starter_* tier → HK equivalents
  starter_sword: 'hk_nail',
  starter_helm: 'hk_mask',
  starter_armor: 'hk_armor',
  starter_band: 'hk_heart',
  starter_shield: 'hk_shield',
  starter_belt: 'hk_scarf',
  starter_sash: 'hk_drape',
  starter_shoulder: 'hk_cloak',
  // Removed vault_* tier → HK equivalents (same slots)
  vault_helm: 'hk_mask',
  vault_armor: 'hk_armor',
  vault_shield: 'hk_shield',
  vault_belt: 'hk_scarf',
  vault_sash: 'hk_drape',
  vault_shoulder: 'hk_cloak',
  // Older legacy IDs from pre-2026 catalogs
  nano_blade: 'hk_nail',
  rusty_saber: 'hk_nail',
  pulse_saber: 'hk_nail',
  arc_cutter: 'hk_nail',
  hyper_cannon: 'hk_nail',
  plasma_scythe: 'hk_nail',
  void_lance: 'hk_nail',
  phantom_edge: 'hk_nail',
  crusher_blade: 'hk_nail',
  baron_pistol: 'hk_nail',
  rng_rifle: 'hk_nail',
  fire_blade: 'hk_nail',
  null_edge: 'hk_nail',
  circuit_breaker: 'hk_nail',
  singularity_edge: 'legendary_sword',
  zeus_bolt: 'legendary_lightning',
  scout_cap: 'hk_mask',
  salvage_helm: 'hk_mask',
  combat_visor: 'hk_mask',
  neural_crown: 'hk_mask',
  void_mask: 'hk_mask',
  gamblers_fallacy: 'hk_mask',
  shadow_visor: 'hk_mask',
  baron_goggles: 'hk_mask',
  rng_helm: 'hk_mask',
  fire_helmet: 'hk_mask',
  titan_helm: 'hk_mask',
  omega_crown: 'hk_mask',
  worn_jacket: 'hk_armor',
  circuit_vest: 'hk_armor',
  nano_plate: 'hk_armor',
  quantum_shell: 'hk_armor',
  shadow_coat: 'hk_armor',
  baron_coat: 'hk_armor',
  rng_vest: 'hk_armor',
  war_frame_delta: 'hk_armor',
  fire_armor: 'hk_armor',
  titan_plate: 'hk_armor',
  aegis_hardsuit: 'hk_armor',
  heart_of_the_warden: 'hk_armor',
  core_sentinel_plate: 'hk_armor',
  worn_band: 'hk_heart',
  signal_ring: 'hk_heart',
  overdrive_core: 'hk_heart',
  pulse_amulet: 'hk_heart',
  void_signet: 'hk_heart',
  titan_core: 'hk_heart',
  baron_sigil: 'hk_heart',
  rng_charm: 'hk_heart',
  rogue_circuit: 'hk_heart',
  aether_sigil: 'legendary_amulet',
  golden_ledger: 'legendary_amulet',
  the_herald_sigil: 'legendary_amulet',
  heart_shield: 'hk_shield',
};

function migrateLegacyItems(player: Player): boolean {
  let changed = false;
  const remap = (inst: EquipmentInstance): EquipmentInstance => {
    const replacement = LEGACY_ITEM_REPLACEMENTS[inst.itemId];
    if (!replacement) return inst;
    changed = true;
    return { ...inst, itemId: replacement };
  };
  for (const slot of Object.keys(player.equipment) as Array<keyof typeof player.equipment>) {
    const inst = player.equipment[slot];
    if (inst) player.equipment[slot] = remap(inst);
  }
  player.equipmentStash = player.equipmentStash.map(remap);
  return changed;
}

type GateState =
  | { kind: 'init' }
  | { kind: 'auth' }
  | { kind: 'loading' }
  /** Picks one of up to 3 characters, or creates a new one. Used for both
   *  authenticated (mode='account') and offline (mode='local') play — the
   *  screen renders the same, only the source/destination of the
   *  character list differs. */
  | { kind: 'selectCharacter'; mode: 'account' | 'local'; characters: CharacterSummary[] }
  /** Name + class form for a specific empty slot. */
  | { kind: 'createCharacter'; mode: 'account' | 'local'; slotIndex: number; existing: CharacterSummary[] }
  | { kind: 'migrate'; localPlayer: Player }
  | { kind: 'ready' }           // authenticated + remote loaded, sync running
  | { kind: 'localReady' }      // anonymous local play, NO Supabase sync
  | { kind: 'unconfigured' };

export function App() {
  // Dev shortcut: visit `#test-3d` to bypass auth and explore the 2.5D engine.
  // Subscribe to hashchange so navigating to/from the route during a session re-renders.
  const [hash, setHash] = useState(typeof window !== 'undefined' ? window.location.hash : '');
  useEffect(() => {
    const onHash = () => setHash(window.location.hash);
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);
  if (hash === '#test-3d') {
    return (
      <Suspense fallback={null}>
        <Test3DScreen />
      </Suspense>
    );
  }
  if (hash === '#tune-equipment') {
    return (
      <Suspense fallback={null}>
        <TuneStudioScreen />
      </Suspense>
    );
  }

  const authStatus = useAuthStore((s) => s.status);
  const authUser = useAuthStore((s) => s.user);
  const authInit = useAuthStore((s) => s.init);

  const setPlayer = useGameStore((s) => s.player); // subscribe to player changes
  const [gate, setGate] = useState<GateState>({ kind: 'init' });
  const [tab, setTab] = useState<Tab>('hero');
  // Track which userId we've already hydrated — prevents re-hydration on token refresh
  const hydratedForUser = useRef<string | null>(null);
  // Cinematic intro: show ONCE per tab session, hold the boot screen until both
  // the cosmetic step-walker finishes AND the gate has resolved past 'init/loading'.
  const [introComplete, setIntroComplete] = useState(false);

  // 1. On mount: bootstrap auth + install telemetry online-flush hook
  useEffect(() => {
    installTelemetryOnlineFlushHook();
    if (!isSupabaseConfigured()) {
      setGate({ kind: 'unconfigured' });
      return;
    }
    void authInit();
  }, [authInit]);

  // 1b. Boot-time preload of every equipment GLB so equipping a new item
  // doesn't trigger a synchronous fetch + parse mid-game. Before this, the
  // first equip of a VK piece left the slot invisible until the user reloaded
  // the page (cache warm). Preload is fire-and-forget; drei.useGLTF caches
  // by path so subsequent useGLTF() calls return the warm scene instantly.
  useEffect(() => {
    void import('@react-three/drei').then(({ useGLTF }) => {
      const seen = new Set<string>();
      for (const def of Object.values(ITEMS)) {
        const path = def.mesh?.path;
        if (path && !seen.has(path)) {
          seen.add(path);
          useGLTF.preload(path);
        }
      }
    });
  }, []);

  // 2. React to auth changes — branch into setup / migrate / ready
  useEffect(() => {
    if (!isSupabaseConfigured()) return;
    if (authStatus === 'init') return;
    if (authStatus === 'anonymous' || authStatus === 'error') {
      hydratedForUser.current = null; // reset so next login re-hydrates
      stopSync();
      // Auto-resume: if there's an active local character on this device,
      // load it directly. Single-character device → bypass select screen.
      // Multi-character device → show the select screen so the user picks.
      // Otherwise → auth.
      const activeId = getActiveLocalCharacterId();
      const locals = listLocalCharacters();
      if (activeId && locals.length === 1) {
        const player = loadLocalCharacter(activeId);
        if (player) {
          sanitizeEquipmentIds(player);
          migrateLegacyItems(player);
          backfillShield(player);
          useGameStore.setState({ player });
          setGate({ kind: 'localReady' });
          return;
        }
      }
      if (locals.length > 0) {
        setGate({ kind: 'selectCharacter', mode: 'local', characters: locals });
      } else {
        setGate({ kind: 'auth' });
      }
      return;
    }
    if (authStatus === 'authenticated' && authUser) {
      // Only hydrate once per userId — token refreshes change the authUser object
      // reference but must NOT re-load from Supabase (would overwrite local progress).
      if (hydratedForUser.current !== authUser.id) {
        hydratedForUser.current = authUser.id;
        void hydrateForUser(authUser.id);
      }
    }
  }, [authStatus, authUser]);

  // 3. Subscribe to gameStore: any player change → markDirty (queue Supabase
  //    sync for account mode) AND syncActiveCharacter (write back into the
  //    local character list so the select-screen renders fresh metadata).
  useEffect(() => {
    const unsub = useGameStore.subscribe((state, prev) => {
      if (state.player === prev.player || !state.player) return;
      markDirty();
      // Local-mode characters have a `local-` prefixed id; only those get
      // mirrored into the multi-char list (account chars live on the server).
      if (state.player.id.startsWith('local-')) {
        syncActiveCharacter(state.player);
      }
    });
    return unsub;
  }, []);

  // 3b. Player-cleared watcher: when player → null while in-game, decide
  //     where to route based on intent flags:
  //       - PENDING_SWITCH_KEY set → "Charakter wechseln": go to the
  //         appropriate select screen (account or local).
  //       - otherwise (account mode): logout flow → auth.
  //       - otherwise (local mode): "Zum Login zurück" → auth.
  useEffect(() => {
    if (gate.kind !== 'localReady' && gate.kind !== 'ready') return;
    const unsub = useGameStore.subscribe((state, prev) => {
      if (!(prev.player && !state.player)) return;
      let switching = false;
      try {
        if (sessionStorage.getItem(PENDING_SWITCH_KEY) === '1') {
          sessionStorage.removeItem(PENDING_SWITCH_KEY);
          switching = true;
        }
      } catch { /* ignore */ }

      if (switching) {
        if (gate.kind === 'localReady') {
          const locals = listLocalCharacters();
          if (locals.length > 0) {
            setGate({ kind: 'selectCharacter', mode: 'local', characters: locals });
            return;
          }
          setGate({ kind: 'auth' });
          return;
        }
        // Account mode — switch to `loading` synchronously so the render-
        // fallback ("gate=ready, player=null → re-hydrate") doesn't fire and
        // re-load the just-cleared character, racing the async listCharacters
        // below. listCharacters takes a network round-trip; without the
        // synchronous gate change, the fallback wins the race.
        if (authUser) {
          setGate({ kind: 'loading' });
          void listCharacters(authUser.id).then((chars) => {
            if (chars.length === 0) setGate({ kind: 'auth' });
            else setGate({ kind: 'selectCharacter', mode: 'account', characters: chars });
          });
          return;
        }
      }
      setGate({ kind: 'auth' });
    });
    return unsub;
  }, [gate.kind, authUser]);

  // 4. Global auto-farmer tick — runs on all tabs, handles offline catch-up
  useEffect(() => {
    const iv = setInterval(() => {
      useGameStore.getState().tickAutoFarmer();
    }, 1000);
    return () => clearInterval(iv);
  }, []);

  // 4b. Daily-quest refresh: assigns today's quest on boot + every minute so
  //     the 04:00 UTC reset rolls a fresh one without requiring a relaunch.
  useEffect(() => {
    useGameStore.getState().refreshDailyQuest();
    const iv = setInterval(() => {
      useGameStore.getState().refreshDailyQuest();
    }, 60_000);
    return () => clearInterval(iv);
  }, []);


  async function hydrateForUser(userId: string): Promise<void> {
    setGate({ kind: 'loading' });
    try {
      let account = await loadAccount(userId);
      if (!account) {
        await createAccount(userId);
        account = { userId, premium: 0, activeCharacterId: null, characters: [] };
      }

      // Pending convert — user clicked "Konto erstellen + Übertragen" while
      // in local mode. Append each local character into the first free
      // account slot. Once all slots are taken, leftover local characters
      // stay on the device (user can prune manually).
      try {
        if (sessionStorage.getItem(PENDING_CONVERT_KEY) === '1') {
          sessionStorage.removeItem(PENDING_CONVERT_KEY);
          account = await convertLocalCharactersIntoAccount(userId, account);
        }
      } catch (err) {
        console.error('[hydrateForUser] convert failed', err);
        // Fall through — user can retry from Settings later.
      }

      // Pre-multi-char saves had a single local Player whose id equalled
      // the user's UUID. Surface that as a one-time migration option.
      // Only relevant when the account is empty AND there are no modern
      // multi-char local saves (convert handles those above).
      if (account.characters.length === 0 && listLocalCharacters().length === 0) {
        const local = readLocalSave();
        if (local && local.id === userId) {
          sanitizeEquipmentIds(local);
          migrateLegacyItems(local);
          backfillShield(local);
          setGate({ kind: 'migrate', localPlayer: local });
          return;
        }
      }

      // Single-character account → auto-select. The select screen would
      // just be one card with a "Continue" button, which is friction.
      if (account.characters.length === 1 && account.activeCharacterId) {
        await loadAndEnterCharacter(account.activeCharacterId, userId);
        return;
      }

      // Multi-character or zero-character → user picks (or creates).
      setGate({ kind: 'selectCharacter', mode: 'account', characters: account.characters });
    } catch (err) {
      console.error('[hydrateForUser] failed', err);
      setGate({ kind: 'auth' });
    }
  }

  /** Load a character from the server, set it as active, start sync,
   *  enter the game. Handles the same conflict-resolution logic the
   *  legacy single-char hydrateForUser had — if the local copy is fresher
   *  than the server snapshot, we keep local and re-push. */
  async function loadAndEnterCharacter(characterId: string, userId: string): Promise<void> {
    const remote = await loadCharacter(characterId);
    if (!remote) {
      // Character was deleted between list-fetch and select — refetch the
      // account so the user sees the up-to-date list.
      const fresh = await loadAccount(userId);
      setGate({ kind: 'selectCharacter', mode: 'account', characters: fresh?.characters ?? [] });
      return;
    }

    const localBefore = useGameStore.getState().player;
    const sameChar = localBefore && localBefore.id === characterId;

    const localTick = sameChar ? (localBefore!.lastTickAt ?? 0) : 0;
    const remoteTick = remote.lastTickAt ?? 0;
    const localIsNewer = sameChar && (localTick - remoteTick > 60_000);
    const localHasMore = localIsNewer &&
      countEquipment(localBefore!) > countEquipment(remote);

    let next = remote;
    let needsResync = sanitizeEquipmentIds(remote);
    if (migrateLegacyItems(remote)) needsResync = true;
    if (backfillShield(remote)) needsResync = true;

    if ((localIsNewer || localHasMore) && localBefore) {
      console.info(
        '[hydrate] local newer than remote, keeping local',
        { localTick, remoteTick, deltaMs: localTick - remoteTick },
      );
      next = { ...localBefore, id: characterId, userId };
      sanitizeEquipmentIds(next);
      if (migrateLegacyItems(next)) needsResync = true;
      if (backfillShield(next)) needsResync = true;
    }

    useGameStore.setState({ player: next });
    await setActiveCharacter(userId, characterId);
    startSync(characterId, () => useGameStore.getState().player);
    if (needsResync) markDirty();
    setGate({ kind: 'ready' });
  }

  function countEquipment(p: Player): number {
    return Object.values(p.equipment).filter(Boolean).length + p.equipmentStash.length;
  }

  /** Take every local-mode character on this device and append it to the
   *  account as a new slot, in characterIndex order. Stops when the
   *  account is full (3 slots) and leaves the remainder on the device.
   *  Returns the refreshed account profile so the caller can continue
   *  routing with the updated character list. */
  async function convertLocalCharactersIntoAccount(
    userId: string,
    account: NonNullable<Awaited<ReturnType<typeof loadAccount>>>,
  ): Promise<typeof account> {
    const localSummaries = listLocalCharacters()
      .slice()
      .sort((a, b) => a.characterIndex - b.characterIndex);
    if (localSummaries.length === 0) return account;

    const usedSlots = new Set(account.characters.map((c) => c.characterIndex));
    let nextSlot = 0;
    let converted = 0;

    for (const summary of localSummaries) {
      // Find next free slot in the account.
      while (nextSlot < MAX_CHARACTER_SLOTS && usedSlots.has(nextSlot)) nextSlot++;
      if (nextSlot >= MAX_CHARACTER_SLOTS) break;

      const localPlayer = loadLocalCharacter(summary.id);
      if (!localPlayer) continue;
      const characterClass: CharacterClass = localPlayer.characterClass ?? 'hollow_knight';

      // Server generates a fresh UUID and seeds the per-table state from
      // localPlayer (equipment, inventory, miners, boosts).
      await createCharacter(userId, nextSlot, characterClass, localPlayer);

      usedSlots.add(nextSlot);
      deleteLocalCharacter(summary.id);
      converted++;
      nextSlot++;
    }

    // Surface the outcome — silent transfers feel like nothing happened,
    // and a "some failed" case needs explicit user awareness.
    if (converted < localSummaries.length) {
      alert(
        `${converted} von ${localSummaries.length} lokalen Charaktere übertragen.\n` +
        `Account-Slots voll (max ${MAX_CHARACTER_SLOTS}). Die restlichen lokalen ` +
        `Charaktere bleiben auf diesem Gerät verfügbar.`,
      );
    } else if (converted > 0) {
      console.info(`[convert] ${converted} lokale Charakter(e) in Account übertragen.`);
    }

    // Refresh: the freshly-created characters need to land in the account
    // object the caller carries forward into the select-screen routing.
    const refreshed = await loadAccount(userId);
    return refreshed ?? account;
  }

  /** Enter local-only play. Reads the local character list — empty list
   *  routes straight into character creation; any existing chars route
   *  into the select screen. */
  function handleEnterLocal(): void {
    const chars = listLocalCharacters();
    if (chars.length === 0) {
      setGate({ kind: 'createCharacter', mode: 'local', slotIndex: 0, existing: chars });
    } else {
      setGate({ kind: 'selectCharacter', mode: 'local', characters: chars });
    }
  }

  /** Pick a character from the select screen → load it + enter the game. */
  async function handleSelectCharacter(characterId: string): Promise<void> {
    if (gate.kind !== 'selectCharacter') return;
    setGate({ kind: 'loading' });
    try {
      if (gate.mode === 'local') {
        const player = loadLocalCharacter(characterId);
        if (!player) {
          setGate({ kind: 'selectCharacter', mode: 'local', characters: listLocalCharacters() });
          return;
        }
        sanitizeEquipmentIds(player);
        migrateLegacyItems(player);
        backfillShield(player);
        setActiveLocalCharacter(characterId);
        useGameStore.setState({ player });
        setGate({ kind: 'localReady' });
      } else {
        if (!authUser) return;
        await loadAndEnterCharacter(characterId, authUser.id);
      }
    } catch (err) {
      console.error('[handleSelectCharacter] failed', err);
      // Fallback: bounce back to select with refreshed list so the user
      // isn't stranded on a loading state.
      if (gate.mode === 'local') {
        setGate({ kind: 'selectCharacter', mode: 'local', characters: listLocalCharacters() });
      } else if (authUser) {
        const acct = await loadAccount(authUser.id);
        setGate({ kind: 'selectCharacter', mode: 'account', characters: acct?.characters ?? [] });
      }
    }
  }

  /** Open the NewCharacterScreen for a given empty slot. */
  function handleOpenCreate(slotIndex: number): void {
    if (gate.kind !== 'selectCharacter') return;
    setGate({ kind: 'createCharacter', mode: gate.mode, slotIndex, existing: gate.characters });
  }

  /** Submit handler from NewCharacterScreen — creates the character in
   *  the appropriate store (Supabase or localStorage), seeds it, sets it
   *  as active, transitions into the game. */
  async function handleCreateCharacter(name: string, characterClass: CharacterClass): Promise<void> {
    if (gate.kind !== 'createCharacter') return;
    // Name uniqueness within the account / local-storage. Case- and
    // whitespace-insensitive so "Felix " and "felix" are recognised as
    // collisions. NewCharacterScreen throws this error → it surfaces the
    // message in the form's error slot.
    const normalized = name.trim().toLowerCase();
    if (gate.existing.some((c) => c.name.trim().toLowerCase() === normalized)) {
      throw new Error(`Name "${name.trim()}" ist bereits vergeben.`);
    }
    const fresh = createPlayer(name.trim());
    fresh.characterClass = characterClass;
    fresh.characterIndex = gate.slotIndex;
    seedStarterKit(fresh);

    if (gate.mode === 'local') {
      // Local: client-generated id, stored entirely in localStorage.
      fresh.id = `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      fresh.userId = null;
      addLocalCharacter(fresh);
      useGameStore.setState({ player: fresh });
      setGate({ kind: 'localReady' });
    } else {
      if (!authUser) return;
      fresh.userId = authUser.id;
      // Server generates the UUID and returns it; reuse that as player.id
      // so subsequent syncs target the right row.
      const newCharacterId = await createCharacter(authUser.id, gate.slotIndex, characterClass, fresh);
      fresh.id = newCharacterId;
      await setActiveCharacter(authUser.id, newCharacterId);
      useGameStore.setState({ player: fresh });
      startSync(newCharacterId, () => useGameStore.getState().player);
      setGate({ kind: 'ready' });
    }
  }

  /** Cancel from NewCharacterScreen — back to the select screen with the
   *  pre-create snapshot of the character list. */
  function handleCancelCreate(): void {
    if (gate.kind !== 'createCharacter') return;
    setGate({ kind: 'selectCharacter', mode: gate.mode, characters: gate.existing });
  }

  /** Delete a character from the select screen — refreshes the list. */
  async function handleDeleteCharacter(characterId: string): Promise<void> {
    if (gate.kind !== 'selectCharacter') return;
    try {
      if (gate.mode === 'local') {
        deleteLocalCharacter(characterId);
        const remaining = listLocalCharacters();
        if (remaining.length === 0) {
          // Last character gone → back to auth (or directly to local-create).
          setGate({ kind: 'auth' });
        } else {
          setGate({ kind: 'selectCharacter', mode: 'local', characters: remaining });
        }
      } else {
        if (!authUser) return;
        await deleteCharacter(characterId);
        const fresh = await listCharacters(authUser.id);
        setGate({ kind: 'selectCharacter', mode: 'account', characters: fresh });
      }
    } catch (err) {
      console.error('[handleDeleteCharacter] failed', err);
    }
  }

  /** Footer link on the select screen: account → sign out; local → drop
   *  back to the auth landing (without wiping the local list). */
  function handleSelectFooter(): void {
    if (gate.kind !== 'selectCharacter') return;
    if (gate.mode === 'account') {
      void useAuthStore.getState().signOut();
    } else {
      setGate({ kind: 'auth' });
    }
  }

  /** Legacy single-character → account migration. Used when an
   *  authenticated user signs in for the first time and the device still
   *  has the old `cve.game.v1` blob with their pre-multi-char progress.
   *  After this, the migrated player becomes slot 0 of the new account. */
  async function handleMigrate(localPlayer: Player): Promise<void> {
    if (!authUser) return;
    setGate({ kind: 'loading' });
    try {
      const characterClass: CharacterClass = localPlayer.characterClass ?? 'hollow_knight';
      const newCharacterId = await createCharacter(authUser.id, 0, characterClass, localPlayer);
      await setActiveCharacter(authUser.id, newCharacterId);
      const player: Player = {
        ...localPlayer,
        id: newCharacterId,
        userId: authUser.id,
        characterIndex: 0,
        characterClass,
      };
      useGameStore.setState({ player });
      clearLocalSave();
      startSync(newCharacterId, () => useGameStore.getState().player);
      setGate({ kind: 'ready' });
    } catch (err) {
      console.error('[handleMigrate] failed', err);
      setGate({ kind: 'auth' });
    }
  }

  async function handleDiscardLocal(): Promise<void> {
    if (!authUser) return;
    clearLocalSave();
    setGate({ kind: 'selectCharacter', mode: 'account', characters: [] });
  }

  // Cleanup sync on unmount
  useEffect(() => () => {
    void flushNow().then(() => stopSync());
  }, []);

  // ── Render branches ─────────────────────────────────────────

  // ── Cinematic intro ────────────────────────────────────────
  // While the intro is still playing, hold the rest of the tree behind it.
  // The intro fires `setIntroComplete(true)` once both:
  //   (a) the cosmetic step-walker animation finished, AND
  //   (b) `ready` flag is true → gate has resolved past init/loading.
  // After that we never re-show the intro for the rest of the tab session.
  if (!introComplete) {
    const gateResolved = gate.kind !== 'init' && gate.kind !== 'loading';
    return <IntroLoader ready={gateResolved} onComplete={() => setIntroComplete(true)} />;
  }

  if (gate.kind === 'unconfigured') {
    // Fallback: let user play offline mode like before
    if (!setPlayer) {
      return <TitleScreen onEnterTerminal={() => setTab('hero')} />;
    }
    const effectiveTab = isTabUnlocked(tab, setPlayer.level) ? tab : 'hero';
    return renderGame(effectiveTab, setTab);
  }

  if (gate.kind === 'init' || gate.kind === 'loading') {
    // Should never hit this branch (intro covers it) but keep as defensive fallback.
    return (
      <div className="app-loading">
        <div className="app-loading__spinner" />
        <div>Lade …</div>
      </div>
    );
  }

  if (gate.kind === 'auth') return <AuthScreen onLocalPlay={handleEnterLocal} />;
  if (gate.kind === 'selectCharacter') {
    return (
      <CharacterSelectScreen
        characters={gate.characters}
        onSelect={(id) => { void handleSelectCharacter(id); }}
        onCreate={handleOpenCreate}
        onDelete={(id) => { void handleDeleteCharacter(id); }}
        footerLinkLabel={gate.mode === 'account' ? 'Abmelden' : 'Zurück zum Login'}
        onFooterLink={handleSelectFooter}
      />
    );
  }
  if (gate.kind === 'createCharacter') {
    return (
      <NewCharacterScreen
        slotIndex={gate.slotIndex}
        onCreate={handleCreateCharacter}
        onCancel={handleCancelCreate}
      />
    );
  }
  if (gate.kind === 'migrate') {
    return (
      <MigrationScreen
        localPlayer={gate.localPlayer}
        onMigrate={() => handleMigrate(gate.localPlayer)}
        onDiscard={handleDiscardLocal}
      />
    );
  }

  // ready OR localReady — same gameplay UI, only difference is that localReady
  // does NOT have profileSync running (no Supabase writes). If by some race
  // the gate landed on *Ready without a player object (Zustand-persist not yet
  // rehydrated, or a race after delete), fall back to the select screen.
  if (!setPlayer) {
    if (gate.kind === 'localReady') {
      const locals = listLocalCharacters();
      if (locals.length > 0) {
        setGate({ kind: 'selectCharacter', mode: 'local', characters: locals });
      } else {
        setGate({ kind: 'auth' });
      }
      return null;
    }
    if (gate.kind === 'ready' && authUser) {
      void hydrateForUser(authUser.id);
      return null;
    }
  }
  // Defensive: if a tab state lingered from a higher-level character and
  // the user just switched to a fresh slot that hasn't unlocked it yet,
  // bounce back to `hero`. The locked-button click-block in BottomNav
  // handles the live navigation case; this catches the switch-character
  // and persisted-state edge cases.
  const playerLevel = setPlayer?.level ?? 1;
  const effectiveTab = isTabUnlocked(tab, playerLevel) ? tab : 'hero';
  return renderGame(effectiveTab, setTab);
}

function renderGame(tab: Tab, setTab: (t: Tab) => void) {
  return (
    <AppShell activeTab={tab} onTabChange={setTab}>
      {tab === 'hero' && <CharacterScreen />}
      {tab === 'terminal' && <TerminalScreen />}
      {tab === 'dungeon' && <DungeonScreen />}
      {tab === 'market' && <MarketScreen />}
      {tab === 'feedback' && <FeedbackScreen />}
      {tab === 'leaderboard' && <LeaderboardScreen />}
    </AppShell>
  );
}

// ── localStorage helpers (for migration) ────────────────────────

const LOCAL_SAVE_KEY = 'cve.game.v1';

function readLocalSave(): Player | null {
  try {
    const raw = localStorage.getItem(LOCAL_SAVE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.state?.player ?? null;
  } catch {
    return null;
  }
}

function clearLocalSave(): void {
  try {
    localStorage.removeItem(LOCAL_SAVE_KEY);
  } catch {
    // ignore
  }
}

// Seed starter kit on a fresh player. P3-tuned: BASIC common-tier gear so
// vault_alpha is an actual learning curve, not a one-shot farm. Players
// progress toward the HK set as their long-term goal. Shield, shoulder,
// belt, sash and accessory still drop progressively from dungeons + bosses.
function seedStarterKit(player: Player): void {
  const SLOT_KIT: Array<[keyof Player['equipment'], string]> = [
    ['weapon', 'starter_blade'],
    ['helmet', 'starter_helmet'],
    ['armor',  'starter_jacket'],
  ];
  for (const [slot, itemId] of SLOT_KIT) {
    player.equipment[slot] = {
      // UUIDv4 — Postgres `equipment_instances.id` is a uuid column, so
      // non-UUID ids cause the sync upsert to fail (after the delete already
      // ran), wiping the row. Use the canonical generator.
      id: randomId(),
      itemId,
      upgradeLevel: 0,
    };
  }
  player.inventory.slots = [
    { itemId: 'scrap', quantity: 10 },
    { itemId: 'energy_cell', quantity: 3 },
  ];
  player.credits = 100;
}
